import { IkeFieldType } from '@ikegps/core-api';
import { type CollectionModel, CollectionModelFieldImage, CollectionModelFieldTrueSize } from '@ikegps/core-shared';
import { T, Translate } from '../../../i18n';
import { AppStore } from '../../../store';
import { DataGrid } from '../components/DataGrid';
import type { ImageReference } from '../components/ImageComponent';
import { ImageGrid } from '../components/ImageGrid';
import { ImageWithTitle } from '../components/ImageWithTitle';
import { TitleBar } from '../components/TitleBar';
import { DataExtractor } from '../DataExtractor';
import { captureReferenceString, compositeURL, hasUserMeasurement, isCaptureHidden } from '../ReportHelpers';
import { ReportBase } from './ReportBase';

export class ReportCompositePhoto extends ReportBase {
	constructor(poles: CollectionModel[]) {
		super(poles);
	}

	buildPDF(): void {
		this.poles.forEach((pole, i) => {
			const job = AppStore.Tag.get(pole.jobId);
			const fullImages = [];
			const tagArr: [string, string][] = [];
			const tagImageGrids: Record<string, ImageReference[]> = {};

			// Title
			this.nodes.push(
				new TitleBar(pole.title || Translate(T.COLLECTION_MISSING_TITLE), job.name, i + 1, this.poles.length)
			);

			pole.forEachField((field) => {
				// Data grid of items outside of subforms
				if (field.parent.parent == null) {
					if (
						field.hasValue() &&
						field.schema.type !== IkeFieldType.FORM &&
						field.schema.type !== IkeFieldType.IMAGE &&
						field.schema.type !== IkeFieldType.TRUE_SIZE
					) {
						const extractedValue = DataExtractor.createPrintableValue(field);
						tagArr.push([extractedValue.title, extractedValue.value]);
					}

					// Image grid
					if (field instanceof CollectionModelFieldImage) {
						const captures = field.getCaptures();

						captures.forEach((capture) => {
							if (!isCaptureHidden(pole, capture.objectId)) {
								const imageReferenceString = captureReferenceString(pole, capture);
								const hasCrop = pole.getCropParameterState(capture.objectId).useInExportCorrections();
								this.images[imageReferenceString] = hasCrop ? compositeURL(pole, capture) : capture.medium.url;
								if (!tagImageGrids[field.schema.displayName]) {
									tagImageGrids[field.schema.displayName] = [];
								}
								tagImageGrids[field.schema.displayName].push({
									reference: imageReferenceString,
									timeStamp: new Date(capture.createdAt),
								});
							}
						});
					}

					// Truesize images
					if (field instanceof CollectionModelFieldTrueSize) {
						const captures = field.getCaptures();
						captures.forEach((capture) => {
							if (!isCaptureHidden(pole, capture.objectId)) {
								if (hasUserMeasurement(pole, capture.objectId)) {
									const imageReferenceString = captureReferenceString(pole, capture);
									this.images[imageReferenceString] = compositeURL(pole, capture);
									fullImages.push([field.schema.displayName, imageReferenceString]);
								}
							}
						});
					}
				}
			});

			// Top level data grid
			this.nodes.push(new DataGrid(tagArr));

			// Tag image in a grid
			this.nodes.push(new ImageGrid(tagImageGrids));

			// Full size composite images
			fullImages.forEach((image) => {
				this.nodes.push(new ImageWithTitle(image[0], { reference: image[1] }));
			});
		});
	}
}
